import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Banner, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { PageHeader } from '@/app/PlaceholderPage';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { mapHeaders, parseCsv } from '@/features/database/csv';
import { INSTRUMENT_ALIASES, INSTRUMENT_TEMPLATE, structureToCsv } from './csv';
import { downloadCsv } from '@/lib/csv';
import { instrumentApi } from './api';

interface PreviewRow {
  rowNumber: number;
  sectionCode: string;
  dimensionCode: string;
  name: string;
  number: number | null;
  weight: number | null;
  severity: 'ready' | 'rejected';
  issues: string[];
}

interface PreviewResult {
  rows: PreviewRow[];
  weightTotals: { code: string; total: number; balanced: boolean }[];
  summary: { ready: number; rejected: number };
}

interface CommitResult {
  version: number;
  createdDraft: boolean;
  dimensions: number;
  indicators: number;
}

/** L-13c — Impor massal struktur instrumen dari CSV. */
export function InstrumentImportPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<unknown[]>([]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [done, setDone] = useState<CommitResult | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['instrument', id, null],
    queryFn: () => instrumentApi.get(id),
  });

  const previewMutation = useMutation({
    mutationFn: (payload: unknown[]) =>
      api.post<PreviewResult>(`/instruments/${id}/import/preview`, { rows: payload }),
    onSuccess: (res) => {
      setPreview(res);
      setBanner(null);
    },
    onError: (err) => setBanner(err instanceof ApiError ? err.message : 'Gagal memeriksa berkas.'),
  });

  const commitMutation = useMutation({
    mutationFn: () => api.post<CommitResult>(`/instruments/${id}/import/commit`, { rows }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['instrument'] });
      queryClient.invalidateQueries({ queryKey: ['instruments'] });
      setDone(res);
    },
  });

  async function onFile(file: File) {
    setFileName(file.name);
    setPreview(null);
    setDone(null);

    const grid = parseCsv(await file.text());
    if (grid.length < 2) {
      setBanner('Berkas kosong atau hanya berisi baris judul.');
      return;
    }

    const index = mapHeaders(grid[0]!, INSTRUMENT_ALIASES);
    if (index.name === undefined) {
      setBanner('Kolom "Indikator" tidak ditemukan. Unduh templat di langkah 1 sebagai acuan.');
      return;
    }

    const parsed = grid.slice(1).map((cells, i) => {
      const row: Record<string, unknown> = { rowNumber: i + 2 };
      for (const [field, col] of Object.entries(index)) row[field] = cells[col] ?? '';
      return row;
    });

    setRows(parsed);
    previewMutation.mutate(parsed);
  }

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  if (done) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Impor Struktur" />
        <Card>
          <div className="flex flex-col items-center py-8 text-center">
            <span className="mb-3 text-4xl" aria-hidden>
              ✅
            </span>
            <h2 className="text-xl font-semibold text-ink">Struktur diimpor</h2>
            <p className="mt-2 max-w-lg text-ink-secondary">
              {done.dimensions} dimensi dan {done.indicators} indikator masuk ke{' '}
              <strong>draf v{done.version}</strong>
              {done.createdDraft && ' yang baru dibuat'}. Versi terbit belum berubah — periksa dulu,
              baru terbitkan.
            </p>
            <div className="mt-6 flex gap-3">
              <Button onClick={() => navigate(`/pengaturan/instrumen/${id}`)}>Periksa Draf</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const canCommit =
    preview !== null && preview.summary.ready > 0 && preview.weightTotals.every((w) => w.balanced);

  return (
    <div className="flex flex-col gap-5">
      <Link
        to={`/pengaturan/instrumen/${id}`}
        className="text-sm text-ink-secondary hover:text-primary"
      >
        ← {data.name}
      </Link>

      <PageHeader
        title="Impor Struktur Instrumen"
        description="Sunting dimensi, indikator, kriteria, dan bobot secara massal lewat Excel."
      />

      {banner && <Banner tone="danger">{banner}</Banner>}

      <Banner tone="info">
        Impor selalu masuk ke <strong>draf</strong>, tidak pernah langsung ke versi terbit. Versi
        yang sedang dipakai penilaian tetap utuh sampai Anda menerbitkan drafnya.
      </Banner>

      <Card>
        <Step n={1} title="Unduh berkas awal" done={Boolean(fileName)}>
          <p className="mb-3 text-ink-secondary">
            Sunting di Excel, simpan sebagai CSV, lalu unggah kembali di langkah 2.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                downloadCsv(`struktur-${data.code.toLowerCase()}.csv`, structureToCsv(data))
              }
            >
              ⭳ Struktur saat ini
            </Button>
            <Button
              variant="ghost"
              onClick={() => downloadCsv('templat-instrumen.csv', INSTRUMENT_TEMPLATE)}
            >
              ⭳ Templat kosong
            </Button>
          </div>
          <p className="mt-3 text-[13px] text-ink-secondary">
            Hampir selalu <strong>Struktur saat ini</strong> yang Anda butuhkan — isinya sudah
            lengkap, tinggal disunting. Templat kosong hanya untuk menyusun dari nol.
          </p>
          <p className="mt-2 text-[13px] text-ink-secondary">
            Kolom <strong>{data.version.evidenceLabel ?? 'MoV'}</strong> boleh berisi beberapa poin:
            pisahkan dengan <strong>titik-koma</strong> (<code>Portofolio; Wawancara</code>) atau
            tekan <strong>Alt+Enter</strong> untuk menulisnya sebagai baris terpisah dalam satu sel.
          </p>
        </Step>
      </Card>

      <Card>
        <Step n={2} title="Unggah berkas CSV" done={Boolean(preview)}>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[var(--radius-input)] border-2 border-dashed border-border px-6 py-10 text-center hover:border-primary hover:bg-primary-soft/40">
            <span className="text-2xl" aria-hidden>
              📎
            </span>
            <span className="font-medium text-ink">{fileName ?? 'Pilih berkas CSV'}</span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onFile(file);
              }}
            />
          </label>
          {previewMutation.isPending && (
            <p className="mt-3 text-sm text-ink-secondary">Memeriksa…</p>
          )}
        </Step>
      </Card>

      {preview && (
        <Card>
          <Step n={3} title="Periksa & simpan" done={false}>
            <div className="mb-4 flex flex-wrap gap-2.5">
              <Tally tone="success" count={preview.summary.ready} label="siap" />
              <Tally tone="danger" count={preview.summary.rejected} label="ditolak" />
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {preview.weightTotals.map((w) => (
                <span
                  key={w.code}
                  className={cn(
                    'tabular rounded-full px-3.5 py-1.5 text-sm font-medium',
                    w.balanced ? 'bg-success-soft text-success' : 'bg-warn-soft text-[#9a6400]',
                  )}
                >
                  Bagian {w.code}: Σ {w.total}
                  {w.balanced ? ' ✓' : ' — harus 100'}
                </span>
              ))}
            </div>

            {preview.rows.filter((r) => r.severity === 'rejected').length > 0 && (
              <ul className="mb-4 flex flex-col gap-2">
                {preview.rows
                  .filter((r) => r.severity === 'rejected')
                  .slice(0, 20)
                  .map((row) => (
                    <li
                      key={row.rowNumber}
                      className="rounded-[var(--radius-input)] border border-danger/30 bg-danger-soft/40 p-3.5"
                    >
                      <p className="text-sm font-medium text-ink">
                        Baris {row.rowNumber}
                        {row.name ? ` · ${row.name}` : ''}
                      </p>
                      {row.issues.map((issue, i) => (
                        <p key={i} className="mt-1 text-[13px] text-danger">
                          {issue}
                        </p>
                      ))}
                    </li>
                  ))}
              </ul>
            )}

            {commitMutation.isError && (
              <div className="mb-4">
                <Banner tone="danger">
                  {commitMutation.error instanceof ApiError
                    ? commitMutation.error.message
                    : 'Gagal menyimpan struktur.'}
                </Banner>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => commitMutation.mutate()}
                loading={commitMutation.isPending}
                disabled={!canCommit}
              >
                Impor {preview.summary.ready} indikator
              </Button>
              {!canCommit && preview.summary.ready > 0 && (
                <p className="text-[13px] text-[#9a6400]">
                  Σ bobot tiap bagian harus tepat 100 sebelum bisa disimpan.
                </p>
              )}
            </div>

            <p className="mt-4 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 text-[13px] text-ink-secondary">
              Seluruh dimensi dan indikator di draf akan <strong>diganti</strong> oleh isi berkas
              ini — bukan digabung. Kategori tidak tersentuh; itu disunting lewat tabnya sendiri.
            </p>
          </Step>
        </Card>
      )}
    </div>
  );
}

function Step({
  n,
  title,
  done,
  children,
}: {
  n: number;
  title: string;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2.5">
        <span
          className={cn(
            'grid size-7 shrink-0 place-items-center rounded-full text-sm font-semibold',
            done ? 'bg-success text-white' : 'bg-primary-soft text-primary',
          )}
        >
          {done ? '✓' : n}
        </span>
        <h2 className="text-[17px] font-semibold text-ink">{title}</h2>
      </div>
      <div className="pl-[38px]">{children}</div>
    </div>
  );
}

function Tally({
  tone,
  count,
  label,
}: {
  tone: 'success' | 'danger';
  count: number;
  label: string;
}) {
  const tones = { success: 'bg-success-soft text-success', danger: 'bg-danger-soft text-danger' };
  const marks = { success: '✓', danger: '✗' };
  return (
    <span className={cn('rounded-full px-3.5 py-1.5 text-sm font-medium', tones[tone])}>
      {marks[tone]} {count} {label}
    </span>
  );
}
